import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    ElementRef,
    inject,
    input,
    output,
    signal,
    untracked,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Select, SelectChangeEvent, SelectModule } from 'primeng/select';
import { FcpFloatLabelComponent, FloatLabelVariant } from '../fcp-float-label';

/** Spec max overlay width (no semantic size token yet). */
const OVERLAY_MAX_WIDTH_PX = 480;

@Component({
    selector: 'fcp-select',
    imports: [CommonModule, FormsModule, SelectModule, FcpFloatLabelComponent],
    styleUrl: './fcp-select.component.less',
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: FcpSelectComponent
        }
    ],
    host: {
        '[attr.data-e2e-hook]': 'e2e()',
        '[class.is-loading]': 'loading()'
    },
    template: `
        <div class="fcp-select-container">
            <ng-template #selectTemplate>
                <p-select
                    [ngModel]="modelValue()"
                    [options]="filteredOptions()"
                    [group]="group()"
                    [filter]="filter()"
                    [filterBy]="filterBy()"
                    [resetFilterOnHide]="resetFilterOnHide()"
                    [filterPlaceholder]="filterPlaceholder()"
                    [placeholder]="showFloatLabel() ? '' : placeholder()"
                    [optionLabel]="optionLabel()"
                    [optionValue]="optionValue()"
                    [optionGroupChildren]="optionGroupChildren()"
                    [showClear]="showClear()"
                    [disabled]="disabled()"
                    [loading]="loading()"
                    [appendTo]="appendTo()"
                    [emptyMessage]="emptyMessage()"
                    [emptyFilterMessage]="emptyFilterMessage()"
                    [scrollHeight]="scrollHeight()"
                    [virtualScroll]="virtualScroll()"
                    [virtualScrollItemSize]="virtualScrollItemSize()"
                    [id]="id()"
                    [ariaLabelledBy]="ariaLabelledBy()"
                    [styleClass]="disabled() ? 'is-disabled' : ''"
                    [class]="'fcp-select'"
                    [size]="size()"
                    [fluid]="fluid()"
                    [panelStyle]="resolvedPanelStyle()"
                    [panelStyleClass]="resolvedPanelStyleClass()"
                    (onChange)="handleChange($event)"
                    (onShow)="handlePanelShow()"
                    (onFilter)="handleFilter($event)"
                >
                    @if (icon()) {
                        <ng-template pTemplate="dropdownicon">
                            <span [class]="icon()!"></span>
                        </ng-template>
                    }
                </p-select>
            </ng-template>
        </div>

        @if (showFloatLabel()) {
            <fcp-float-label [variant]="selectFloatLabelVariant">
                <ng-container *ngTemplateOutlet="selectTemplate" />
                <label [for]="id()">{{ placeholder() }}</label>
            </fcp-float-label>
        } @else {
            <ng-container *ngTemplateOutlet="selectTemplate" />
        }
    `
})
export class FcpSelectComponent implements ControlValueAccessor {
    private readonly _virtualScrollItemSize = 36;
    private readonly _scrollHeight = '200px';
    readonly selectFloatLabelVariant = FloatLabelVariant.In;

    private readonly host = inject(ElementRef<HTMLElement>);
    @ViewChild(Select) private selectRef?: Select;

    private readonly overlayContentWidthPx = signal<number | null>(null);
    private readonly overlayListHeightPx = signal<number | null>(null);

    constructor() {
        // appendTo defaults to 'body', so an already-open panel isn't covered by the
        // host's dimmed/pointer-events-none loading style — close it explicitly instead.
        effect(() => {
            if (this.loading()) {
                this.selectRef?.hide();
            }
        });

        effect(() => {
            const options = this.options();
            const optionLabel = this.optionLabel();
            const group = this.group();
            const optionGroupChildren = this.optionGroupChildren();
            const itemSize = this.virtualScrollItemSize();
            const scrollHeight = this.scrollHeight();
            untracked(() => {
                queueMicrotask(() =>
                    this.syncOverlaySize(options, optionLabel, group, optionGroupChildren, itemSize, scrollHeight)
                );
            });
        });
    }

    e2e = input<string | null>(null);
    options = input<unknown[]>([]);
    group = input<boolean>(false);
    placeholder = input<string>('');
    optionLabel = input<string>('label');
    optionValue = input<string>('value');
    optionGroupChildren = input<string>('items');
    disabled = input<boolean>(false);
    loading = input<boolean>(false);
    showClear = input<boolean>(false);
    emptyMessage = input<string>('');
    emptyFilterMessage = input<string>('');
    floatLabel = input<boolean>(false);
    filter = input<boolean>(false);
    filterBy = input<string>('');
    filterPlaceholder = input<string>('');
    resetFilterOnHide = input<boolean>(false);
    scrollHeight = input<string>(this._scrollHeight);
    virtualScroll = input<boolean>(false);
    virtualScrollItemSize = input<number>(this._virtualScrollItemSize);
    id = input<string | undefined>(undefined);
    ariaLabelledBy = input<string | undefined>(undefined);
    size = input<SelectSizes>(SelectSizes.Normal);
    fluid = input<boolean>(false);
    panelStyle = input<Record<string, string | number>>({});
    panelStyleClass = input<string>('');
    appendTo = input<string | HTMLElement>('body');
    icon = input<string | undefined>(undefined);

    readonly showFloatLabel = computed(() => this.floatLabel() && this.size() !== SelectSizes.Small);
    readonly resolvedPanelStyleClass = computed(() => this.panelStyleClass().trim());
    readonly resolvedPanelStyle = computed(() => {
        const style: Record<string, string | number> = {
            ...this.panelStyle()
        };
        const contentWidth = this.overlayContentWidthPx();
        if (contentWidth != null) {
            style['--fcp-select-content-width'] = `${contentWidth}px`;
        }
        const listHeight = this.overlayListHeightPx();
        if (listHeight != null) {
            style['--fcp-select-list-height'] = `${listHeight}px`;
        }
        return style;
    });

    onChange = output<unknown>();

    private _value = signal<unknown>(null);
    private _onChange = signal<(value: unknown) => void>(() => {});
    private _onTouched = signal<() => void>(() => {});

    modelValue = computed(() => this._value());
    filteredOptions = computed(() => this.options());

    writeValue(value: unknown): void {
        this._value.set(value);
    }

    registerOnChange(fn: (value: unknown) => void): void {
        this._onChange.set(fn);
    }

    registerOnTouched(fn: () => void): void {
        this._onTouched.set(fn);
    }

    handleChange(event: SelectChangeEvent): void {
        this._value.set(event.value);
        this._onChange()(event.value);
        this._onTouched()();
        this.onChange.emit(event.value);
    }

    handlePanelShow(): void {
        const options = this.getVisibleOptions();
        const optionCount = this.countListOptions(options, this.group(), this.optionGroupChildren());

        if (optionCount === 0) {
            this.seedOverlayWidthFromTrigger();
            this.seedOverlayHeightForEmpty();
        } else {
            this.syncOverlaySize(
                options,
                this.optionLabel(),
                this.group(),
                this.optionGroupChildren(),
                this.virtualScrollItemSize(),
                this.scrollHeight()
            );
        }
    }

    handleFilter(): void {
        queueMicrotask(() => {
            const options = this.getVisibleOptions();
            this.syncOverlaySize(
                options,
                this.optionLabel(),
                this.group(),
                this.optionGroupChildren(),
                this.virtualScrollItemSize(),
                this.scrollHeight()
            );
        });
    }

    private getVisibleOptions(): unknown[] {
        const visible = this.selectRef?.visibleOptions?.();
        return Array.isArray(visible) ? visible : this.options();
    }

    private syncOverlaySize(
        options: unknown[],
        optionLabel: string,
        group: boolean,
        optionGroupChildren: string,
        itemSize: number,
        scrollHeight: string
    ): void {
        if (typeof document === 'undefined') {
            return;
        }

        const optionCount = this.countListOptions(options, group, optionGroupChildren);
        if (optionCount === 0) {
            this.seedOverlayHeightForEmpty();
            return;
        }

        const labels = this.collectOptionLabels(options, optionLabel, group, optionGroupChildren);
        const triggerWidth = this.getTriggerWidth();
        const longestLabelWidth = this.measureLongestLabelWidth(labels);
        const contentWidth = longestLabelWidth + this.getOptionChromeWidth();
        const width = this.resolveOverlayWidth(triggerWidth, contentWidth);

        const maxListHeight = this.parseCssLengthPx(scrollHeight, 200);
        const listHeight = Math.min(maxListHeight, optionCount * itemSize);

        this.overlayContentWidthPx.set(Math.ceil(width));
        this.overlayListHeightPx.set(Math.ceil(listHeight));
        this.realignOpenOverlay();
    }

    private seedOverlayWidthFromTrigger(): void {
        if (typeof document === 'undefined') {
            return;
        }
        const triggerWidth = this.getTriggerWidth();
        if (triggerWidth <= 0) {
            return;
        }
        this.overlayContentWidthPx.set(Math.ceil(this.resolveOverlayWidth(triggerWidth, triggerWidth)));
        this.realignOpenOverlay();
    }

    /**
     * Match the trigger width. Grow for long labels up to 480px only when the trigger is
     * narrower than that cap. If a wider-than-trigger panel cannot fit left-aligned under
     * the field, match the trigger instead so DomHandler does not shift the overlay past
     * the field edges (padded small screens).
     */
    private resolveOverlayWidth(triggerWidth: number, contentWidth: number): number {
        // Cap applies to content-driven growth, not to matching a wider trigger.
        const width = Math.max(triggerWidth, Math.min(contentWidth, OVERLAY_MAX_WIDTH_PX));

        if (typeof window === 'undefined') {
            return width;
        }

        const triggerLeft = this.getTriggerElement()?.getBoundingClientRect().left ?? 0;
        const spaceRight = Math.max(0, window.innerWidth - triggerLeft);

        if (width > spaceRight) {
            return Math.min(triggerWidth, spaceRight);
        }
        return Math.min(width, spaceRight);
    }

    private seedOverlayHeightForEmpty(): void {
        this.overlayListHeightPx.set(this.virtualScrollItemSize());
        this.realignOpenOverlay();
    }

    private countListOptions(options: unknown[], group: boolean, optionGroupChildren: string): number {
        if (!group) {
            return options.length;
        }
        let count = 0;
        for (const option of options) {
            if (option && typeof option === 'object') {
                const children = (option as Record<string, unknown>)[optionGroupChildren];
                if (Array.isArray(children)) {
                    count += children.length;
                }
            }
        }
        return count;
    }

    private parseCssLengthPx(value: string, fallback: number): number {
        const trimmed = value.trim();
        const amount = Number.parseFloat(trimmed);
        if (!Number.isFinite(amount)) {
            return fallback;
        }
        if (trimmed.endsWith('rem')) {
            const rootFontSize =
                typeof document !== 'undefined'
                    ? Number.parseFloat(getComputedStyle(document.documentElement).fontSize) || 16
                    : 16;
            return amount * rootFontSize;
        }
        if (trimmed.endsWith('vh')) {
            if (typeof window === 'undefined' || !Number.isFinite(window.innerHeight)) {
                return fallback;
            }
            return (amount / 100) * window.innerHeight;
        }
        return amount;
    }

    private collectOptionLabels(
        options: unknown[],
        optionLabel: string,
        group: boolean,
        optionGroupChildren: string
    ): string[] {
        const labels: string[] = [];
        for (const option of options) {
            if (group && option && typeof option === 'object') {
                const children = (option as Record<string, unknown>)[optionGroupChildren];
                if (Array.isArray(children)) {
                    labels.push(...this.collectOptionLabels(children, optionLabel, false, optionGroupChildren));
                }
                continue;
            }
            const label = this.resolveOptionLabel(option, optionLabel);
            if (label) {
                labels.push(label);
            }
        }
        return labels;
    }

    private resolveOptionLabel(option: unknown, optionLabel: string): string {
        if (option == null) {
            return '';
        }
        if (typeof option === 'string' || typeof option === 'number') {
            return String(option);
        }
        if (typeof option === 'object') {
            const record = option as Record<string, unknown>;
            const value = optionLabel ? record[optionLabel] : (record['label'] ?? option);
            return value == null ? '' : String(value);
        }
        return '';
    }

    private measureLongestLabelWidth(labels: string[]): number {
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        if (!context) {
            return 0;
        }

        const rootStyle = getComputedStyle(document.documentElement);
        const bodyStyle = getComputedStyle(document.body);
        const fontSize =
            rootStyle.getPropertyValue('--typography-body-m-size-regular').trim() || bodyStyle.fontSize || '14px';
        const fontWeight =
            rootStyle.getPropertyValue('--typography-body-m-weight-regular').trim() || bodyStyle.fontWeight || '400';
        const fontFamily = bodyStyle.fontFamily || 'sans-serif';
        context.font = `${fontWeight} ${fontSize} ${fontFamily}`;

        let longest = 0;
        for (const label of labels) {
            longest = Math.max(longest, context.measureText(label).width);
        }
        return longest;
    }

    /** Option padding-x + list padding (no checkbox on default select). */
    private getOptionChromeWidth(): number {
        const sizing = (token: string, fallback: number) => {
            const raw = getComputedStyle(document.documentElement).getPropertyValue(token).trim();
            const parsed = Number.parseFloat(raw);
            return Number.isFinite(parsed) ? parsed : fallback;
        };
        const optionPaddingX = sizing('--sizing-12', 12) * 2;
        const listPaddingX = sizing('--sizing-4', 4) * 2;
        return optionPaddingX + listPaddingX;
    }

    private getTriggerElement(): HTMLElement | null {
        return this.host.nativeElement.querySelector('.p-select') as HTMLElement | null;
    }

    private getTriggerWidth(): number {
        return this.getTriggerElement()?.offsetWidth || this.host.nativeElement.offsetWidth || 0;
    }

    private realignOpenOverlay(): void {
        if (!this.selectRef?.overlayVisible) {
            return;
        }
        requestAnimationFrame(() => {
            this.selectRef?.overlayViewChild?.alignOverlay();
        });
    }
}

export enum SelectSizes {
    Small = 'small',
    Normal = null
}
